import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import normalizePrice from "@/utils/normalizePrice";

export type TopupPaymentStatus = "processing" | "success" | "failed";
export type TopupPaymentFailureMessage = Partial<
  Record<"en" | "zh", string>
>;

interface Props {
  status: TopupPaymentStatus | null;
  amount: number | null;
  failureMessage?: TopupPaymentFailureMessage | null;
  onBack: () => void;
}

export default function TopupPaymentStatusModal({
  status,
  amount,
  failureMessage,
  onBack,
}: Props) {
  const { t, i18n } = useTranslation();
  const isProcessing = status === "processing";
  const isSuccess = status === "success";
  const failureMessageLanguage = (
    i18n.resolvedLanguage || i18n.language
  ).startsWith("zh")
    ? "zh"
    : "en";
  const localizedFailureMessage =
    failureMessage?.[failureMessageLanguage] ||
    failureMessage?.en ||
    failureMessage?.zh;

  return (
    <Modal
      open={Boolean(status)}
      footer={null}
      closable={false}
      maskClosable={false}
      centered
      width={360}
      styles={{ content: { borderRadius: 8, padding: 0 } }}
    >
      <div className="flex min-h-[248px] flex-col items-center justify-center px-8 text-center">
        <span
          aria-hidden="true"
          className={`mb-5 flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
            isSuccess
              ? "bg-[#d1fadf]"
              : isProcessing
                ? "bg-transparent"
                : "bg-[#fee4e2]"
          }`}
        >
          {isProcessing ? (
            <span className="relative h-9 w-9 animate-spin rounded-full">
              <span className="absolute inset-0 rounded-full border-[3.6px] border-[#FF621D] opacity-30" />
              <span
                className="absolute inset-0 rounded-full bg-[conic-gradient(#FF621D_0deg_90deg,transparent_90deg_360deg)]"
                style={{
                  WebkitMask:
                    "radial-gradient(farthest-side, transparent calc(100% - 3.6px), #000 calc(100% - 3.6px))",
                  mask: "radial-gradient(farthest-side, transparent calc(100% - 3.6px), #000 calc(100% - 3.6px))",
                }}
              />
            </span>
          ) : isSuccess ? (
            <svg
              className="h-[22px] w-[22px]"
              fill="none"
              viewBox="0 0 22 22"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.3337 5.5L8.25033 15.5833L3.66699 11"
                stroke="#1C7A41"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.0625}
              />
            </svg>
          ) : (
            <svg
              className="h-7 w-7"
              fill="none"
              viewBox="0 0 28 28"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.7361 6.91199L6.91211 20.736M6.91211 6.91199L20.7361 20.736"
                stroke="#D92D20"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.304}
              />
            </svg>
          )}
        </span>
        <h3 className="m-0 text-base font-semibold text-[#171717]">
          {isProcessing
            ? t("components.chargeModal.topupStatus.processing.title", {
                defaultValue: "Processing payment",
              })
            : isSuccess
              ? t("components.chargeModal.topupStatus.success.title", {
                  defaultValue: "Payment successful",
                })
              : t("components.chargeModal.topupStatus.failed.title", {
                  defaultValue: "Payment failed",
                })}
        </h3>
        <p className="mt-2 text-xs text-[#737373]">
          {isProcessing
            ? t("components.chargeModal.topupStatus.processing.description", {
                defaultValue: "We're processing your payment. Please wait a moment.",
              })
            : isSuccess
              ? t("components.chargeModal.topupStatus.success.description", {
                  balance_amount:
                    typeof amount === "number" ? normalizePrice(amount) : "",
                  defaultValue:
                    "{balance_amount} has been successfully added to your account.",
                })
              : localizedFailureMessage ||
                t("components.chargeModal.topupStatus.failed.description", {
                  defaultValue: "Your payment could not be completed. Please try again.",
                })}
        </p>
        {!isProcessing && (
          <button
            type="button"
            onClick={onBack}
            className="mt-4 h-8 rounded-md border border-[#d0d5dd] px-4 text-xs font-medium text-[#344054]"
          >
            {t("components.chargeModal.topupStatus.back", {
              defaultValue: "Back",
            })}
          </button>
        )}
      </div>
    </Modal>
  );
}
